import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Users, HardDrive, Mail, Send,
  ArrowUpRight, Lock, Check, Infinity, ChevronDown,
} from 'lucide-react';
import { usePlatformStore } from '@/lib/store';
import { Spinner } from '@/components/shared/UIComponents';
import { ContextualHelp } from '@/components/help/ContextualHelp';
import { FEATURE_META, APP_FEATURE_KEYS, PLATFORM_FEATURE_KEYS } from '@/lib/constants';
import { fetchPlanFeatureDefaults, fetchActivePlans } from '@/lib/api';
import type { TenantPlan } from '@/types';

// ── Types ───────────────────────────────────────────────────────────────────

interface PlanDefault {
  enabled: boolean;
  limit: number | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatStorage(mb: number): string {
  if (mb >= 1000) return `${(mb / 1000).toFixed(mb % 1000 === 0 ? 0 : 1)} GB`;
  return `${mb} MB`;
}

function formatCampaignLimit(value: number): string {
  if (value === -1) return 'Unlimited';
  if (value === 0) return 'None';
  return String(value);
}

function formatPrice(cents: number): string {
  if (cents === 0) return 'Free';
  const dollars = cents / 100;
  return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}/seat/month`;
}

/** Group feature keys by category, sorted alphabetically by label */
function groupByCategory(keys: string[]): [string, string[]][] {
  const g: Record<string, string[]> = {};
  for (const k of keys) {
    const cat = FEATURE_META[k]?.category;
    if (!cat) continue;
    if (!g[cat]) g[cat] = [];
    g[cat].push(k);
  }
  for (const cat of Object.keys(g)) {
    g[cat].sort((a, b) => (FEATURE_META[a]?.label || '').localeCompare(FEATURE_META[b]?.label || ''));
  }
  return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
}

/** Format a numeric limit for display */
function formatLimit(limit: number | null | undefined): string {
  if (limit === null || limit === undefined) return 'Unlimited';
  if (limit === -1) return 'Unlimited';
  return String(limit);
}

// ── Component ────────────────────────────────────────────────────────────────

export function FeaturesPage({ tenantId }: { tenantId: string }) {
  const { selectedTenant: tenant } = usePlatformStore();
  const plan = tenant?.plan as TenantPlan | undefined;

  const [planDefaultsMap, setPlanDefaultsMap] = useState<Record<string, PlanDefault>>({});
  const [allPlans, setAllPlans] = useState<TenantPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Per-category collapse state. Tier-prefixed keys ("app:Core" /
  // "platform:Branding") so collisions are impossible.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const toggleCategory = (key: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Fetch plan feature defaults from the DB
  useEffect(() => {
    if (!plan?.id) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const [defaults, plans] = await Promise.all([
          fetchPlanFeatureDefaults(plan.id),
          fetchActivePlans(),
        ]);

        if (cancelled) return;

        const map: Record<string, PlanDefault> = {};
        for (const d of defaults) {
          map[d.feature_key] = { enabled: d.is_enabled, limit: d.limit_value ?? null };
        }
        setPlanDefaultsMap(map);
        setAllPlans(plans.filter((p) => p.is_active));
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [plan?.id]);

  // ── Loading / error states ──────────────────────────────────────────────

  if (!plan || loading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-panel p-6 text-center">
        <p className="text-red-600 dark:text-red-400 text-sm">Failed to load plan details: {error}</p>
      </div>
    );
  }

  // ── Derived data ────────────────────────────────────────────────────────

  const featureKeys = Object.keys(FEATURE_META);
  const currentRank = plan.rank ?? 0;
  const nextPlan = allPlans
    .filter((p) => p.rank > currentRank)
    .sort((a, b) => a.rank - b.rank)[0] ?? null;

  const currentIncluded = featureKeys.filter((k) => planDefaultsMap[k]?.enabled === true).length;
  const totalFeatures = featureKeys.length;

  const appCategories = groupByCategory(APP_FEATURE_KEYS);
  const platformCategories = groupByCategory(PLATFORM_FEATURE_KEYS);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-bold text-surface-900">Your Plan</h1>
        <p className="text-sm text-surface-500 mt-1">
          Overview of features and limits included in your current plan.
        </p>
      </div>

      {/* ── Plan Summary Card ─────────────────────────────────────────────── */}
      <div className="glass-panel p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-surface-100">
              <Shield className="w-5 h-5 text-brand-600 dark:text-brand-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-surface-900">
                  {plan.display_name}
                </h2>
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                  style={{ backgroundColor: plan.color || '#64748b' }}
                >
                  {plan.display_name}
                </span>
              </div>
              {plan.description && (
                <p className="text-sm text-surface-500 mt-0.5">{plan.description}</p>
              )}
            </div>
          </div>

          <Link
            to={`/admin/tenants/${tenantId}/manage/billing`}
            className="flex items-center gap-1.5 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-300 transition-colors"
          >
            Manage Plan
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-overlay/[0.04] border border-overlay/[0.06] rounded-xl p-4 text-center">
            <Users className="w-4 h-4 text-brand-600 dark:text-brand-400 mx-auto mb-1.5" />
            <div className="text-lg font-bold text-surface-900">
              {plan.max_users === null ? 'Unlimited' : plan.max_users}
            </div>
            <div className="text-xs text-surface-500">Max Users</div>
          </div>

          <div className="bg-overlay/[0.04] border border-overlay/[0.06] rounded-xl p-4 text-center">
            <HardDrive className="w-4 h-4 text-brand-600 dark:text-brand-400 mx-auto mb-1.5" />
            <div className="text-lg font-bold text-surface-900">
              {formatStorage(plan.max_storage_mb)}
            </div>
            <div className="text-xs text-surface-500">Storage</div>
          </div>

          <div className="bg-overlay/[0.04] border border-overlay/[0.06] rounded-xl p-4 text-center">
            <Mail className="w-4 h-4 text-brand-600 dark:text-brand-400 mx-auto mb-1.5" />
            <div className="text-lg font-bold text-surface-900">
              {formatCampaignLimit(plan.max_campaigns_per_month)}
            </div>
            <div className="text-xs text-surface-500">Campaigns/Mo</div>
          </div>

          <div className="bg-overlay/[0.04] border border-overlay/[0.06] rounded-xl p-4 text-center">
            <Send className="w-4 h-4 text-brand-600 dark:text-brand-400 mx-auto mb-1.5" />
            <div className="text-lg font-bold text-surface-900">
              {plan.max_campaigns_per_month === 0
                ? 'N/A'
                : plan.max_campaign_recipients}
            </div>
            <div className="text-xs text-surface-500">Max Recipients</div>
          </div>
        </div>

        {/* Pricing */}
        <div className="mt-4 pt-4 border-t border-overlay/[0.06] flex items-center justify-between">
          <div className="text-sm text-surface-500">
            Pricing:{' '}
            <span className="font-semibold text-surface-900">
              {formatPrice(plan.price_per_seat_monthly)}
            </span>
          </div>
          <div className="text-xs text-surface-500">
            {currentIncluded} of {totalFeatures} features included
          </div>
        </div>
      </div>

      {/* ── Feature Overview Table Card ───────────────────────────────────── */}
      <div className="glass-panel overflow-hidden">
        {/* Table header */}
        <div className="px-5 py-3 border-b border-overlay/[0.06] flex items-center justify-between">
          <h3 className="text-sm font-semibold text-surface-900">Feature Overview</h3>
          <span className="text-xs text-surface-500">
            {currentIncluded} included &middot; {totalFeatures - currentIncluded} locked
          </span>
        </div>

        {/* Column headers */}
        <div className="grid grid-cols-[1fr_100px_32px] sm:grid-cols-[1fr_2fr_100px_48px] px-5 py-2 border-b border-overlay/[0.06] bg-overlay/[0.02]">
          <span className="text-[10px] font-semibold text-surface-500 uppercase tracking-wider">Feature</span>
          <span className="hidden sm:block text-[10px] font-semibold text-surface-500 uppercase tracking-wider">Description</span>
          <span className="text-[10px] font-semibold text-surface-500 uppercase tracking-wider text-center">Limit</span>
          <span className="text-[10px] font-semibold text-surface-500 uppercase tracking-wider text-center">Status</span>
        </div>

        {/* App Features (B2C) */}
        {appCategories.length > 0 && (
          <>
            <div className="px-5 py-2 bg-overlay/[0.03] border-b border-overlay/[0.04]">
              <span className="text-[10px] font-bold text-surface-600 uppercase tracking-wider">App Features (B2C)</span>
            </div>

            {appCategories.map(([cat, keys]) => {
              const collapseKey = `app:${cat}`;
              const isCollapsed = collapsed.has(collapseKey);
              const enabledInCat = keys.filter(k => planDefaultsMap[k]?.enabled === true).length;
              return (
                <FeatureCategoryAccordion
                  key={collapseKey}
                  cat={cat}
                  isCollapsed={isCollapsed}
                  onToggle={() => toggleCategory(collapseKey)}
                  count={keys.length}
                  enabledCount={enabledInCat}
                  keys={keys}
                  planDefaultsMap={planDefaultsMap}
                />
              );
            })}
          </>
        )}

        {/* Platform Features (B2B) */}
        {platformCategories.length > 0 && (
          <>
            <div className="px-5 py-2 bg-overlay/[0.03] border-b border-overlay/[0.04]">
              <span className="text-[10px] font-bold text-surface-600 uppercase tracking-wider">Platform Features (B2B)</span>
            </div>

            {platformCategories.map(([cat, keys]) => {
              const collapseKey = `platform:${cat}`;
              const isCollapsed = collapsed.has(collapseKey);
              const enabledInCat = keys.filter(k => planDefaultsMap[k]?.enabled === true).length;
              return (
                <FeatureCategoryAccordion
                  key={collapseKey}
                  cat={cat}
                  isCollapsed={isCollapsed}
                  onToggle={() => toggleCategory(collapseKey)}
                  count={keys.length}
                  enabledCount={enabledInCat}
                  keys={keys}
                  planDefaultsMap={planDefaultsMap}
                />
              );
            })}
          </>
        )}
      </div>

      {/* ── Upgrade Comparison ────────────────────────────────────────────── */}
      {nextPlan && (
        <div className="glass-panel p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <ArrowUpRight className="w-4 h-4 text-brand-600 dark:text-brand-400 shrink-0" />
                <h3 className="text-sm font-semibold text-surface-900">
                  Upgrade to {nextPlan.display_name}
                </h3>
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                  style={{ backgroundColor: nextPlan.color || '#64748b' }}
                >
                  {nextPlan.display_name}
                </span>
              </div>
              <p className="text-xs text-surface-500">
                Unlock {totalFeatures - currentIncluded} more feature{totalFeatures - currentIncluded !== 1 ? 's' : ''} and
                gain access to higher limits.
                {nextPlan.price_per_seat_monthly > 0 && (
                  <> Starting at {formatPrice(nextPlan.price_per_seat_monthly)}.</>
                )}
              </p>
            </div>
            <Link
              to={`/admin/tenants/${tenantId}/manage/platform-plans`}
              className="shrink-0 px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium transition-colors"
            >
              View Plans
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

/** A category accordion: header is a button toggling the body. Both the
 *  category list and the feature list inside are alphabetical (the parent
 *  groupByCategory helper sorts both). */
function FeatureCategoryAccordion({
  cat, isCollapsed, onToggle, count, enabledCount, keys, planDefaultsMap,
}: {
  cat: string;
  isCollapsed: boolean;
  onToggle: () => void;
  count: number;
  enabledCount: number;
  keys: string[];
  planDefaultsMap: Record<string, PlanDefault>;
}) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full px-5 py-2 border-b border-overlay/[0.04] bg-overlay/[0.015] flex items-center gap-2 hover:bg-overlay/[0.04] transition-colors"
      >
        <ChevronDown
          size={12}
          className={`text-surface-500 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
        />
        <span className="text-[10px] font-semibold text-brand-600 dark:text-brand-400 uppercase tracking-wider">
          {cat}
        </span>
        <span className="ml-auto text-[10px] text-surface-500">
          {enabledCount} of {count} included
        </span>
      </button>

      {!isCollapsed && keys.map((key) => {
        const meta = FEATURE_META[key];
        const defaultInfo = planDefaultsMap[key];
        const inPlan = defaultInfo?.enabled === true;
        const hasLimit = meta.hasLimit && inPlan;

        return (
          <div
            key={key}
            className={`grid grid-cols-[1fr_100px_32px] sm:grid-cols-[1fr_2fr_100px_48px] px-5 py-2.5 border-b border-overlay/[0.04] items-center ${
              !inPlan ? 'opacity-50' : ''
            }`}
          >
            <div className="min-w-0">
              <span className="text-sm font-medium text-surface-800">{meta.label}</span>
              <p className="text-xs text-surface-500 mt-0.5 sm:hidden truncate">{meta.description}</p>
            </div>

            <span className="hidden sm:block text-xs text-surface-500 pr-2 truncate">{meta.description}</span>

            <div className="flex justify-center">
              {!inPlan ? (
                <span className="text-[10px] text-surface-500">&mdash;</span>
              ) : hasLimit ? (
                <span className="text-xs font-semibold text-brand-600 dark:text-brand-400 tabular-nums">
                  {defaultInfo?.limit !== null && defaultInfo?.limit !== undefined
                    ? formatLimit(defaultInfo.limit)
                    : <ContextualHelp helpKey="unlimited-symbol" iconSize={10}><Infinity className="w-3.5 h-3.5 inline text-brand-600 dark:text-brand-400" /></ContextualHelp>}
                </span>
              ) : (
                <span className="text-[10px] text-surface-500">&mdash;</span>
              )}
            </div>

            <div className="flex justify-center">
              {inPlan ? (
                <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center">
                  <Check className="w-3 h-3 text-emerald-400" />
                </div>
              ) : (
                <ContextualHelp helpKey="locked-feature" iconSize={10}>
                  <div className="w-5 h-5 rounded-full bg-surface-200/30 flex items-center justify-center">
                    <Lock className="w-3 h-3 text-surface-500" />
                  </div>
                </ContextualHelp>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
